import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin, Facebook, Menu, X, Info, Star, Warehouse, Boxes, HardHat } from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import ConstructionInquiryForm from "@/components/ConstructionInquiryForm";

/*
  E.G. & I Construction Corporation — concept site. Established 1993; corporate
  industrial identity built around real, branded project photography (warehouse,
  grain silo, pipeline and foundation work), with three decades of history front
  and center.
*/

const STEEL = "#2B3A4A";
const ORANGE = "#E0611C";
const CREAM = "#F5F4EF";
const INK = "#1A1A1A";

const FB = "https://www.facebook.com/EGIConstructionCorp/";
const NAME = "E.G. & I Construction Corporation";
const SLUG = "egi-construction-concept";
const P = "/construction/egi";

const fadeUp = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function EgiConcept() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 8);
    s(); window.addEventListener("scroll", s);
    return () => window.removeEventListener("scroll", s);
  }, []);

  const links = [["Capabilities", "#capabilities"], ["Projects", "#projects"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Titillium Web', sans-serif" }} data-testid="egi-concept-root">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Titillium+Web:wght@400;500;600;700;900&family=Rajdhani:wght@600;700&display=swap');
        #top h1, #top h2, #top h3 { font-family: 'Rajdhani', sans-serif; letter-spacing: 0.01em; }
      `}</style>

      <div data-testid="construction-disclaimer" className="bg-[#111820] text-white text-center text-xs py-2 relative z-50">
        <span className="inline-flex items-center gap-2 px-4">
          <Info size={13} className="shrink-0 text-white/60" />
          Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {NAME}.
        </span>
      </div>

      <header className={`sticky top-0 z-40 transition-shadow ${scrolled ? "shadow-[0_4px_20px_rgba(0,0,0,0.25)]" : ""}`} style={{ background: STEEL }}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="egi-logo" className="flex items-center gap-3">
            <img src={`${P}/logo.jpg`} alt={NAME} className="h-11 w-11 rounded-full object-cover" />
            <span className="text-lg text-white font-black tracking-tight">E.G. &amp; I</span>
          </a>
          <div className="hidden lg:flex items-center gap-8">
            {links.map(([l, h]) => (
              <a key={h} href={h} className="text-sm font-semibold text-white/75 hover:text-white">{l}</a>
            ))}
          </div>
          <a href="#contact" data-testid="egi-header-cta" className="hidden sm:inline-flex px-5 py-2.5 text-sm font-bold text-white" style={{ background: ORANGE }}>
            Request a Proposal
          </a>
          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="egi-menu-toggle" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
        {open && (
          <div className="lg:hidden px-5 pb-4 space-y-1" style={{ background: STEEL }}>
            {links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-white font-semibold">{l}</a>)}
            <a href="#contact" onClick={() => setOpen(false)} data-testid="egi-mobile-cta" className="block mt-2 text-center px-5 py-3 text-sm font-bold text-white" style={{ background: ORANGE }}>Request a Proposal</a>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-[84vh] flex items-end" data-testid="egi-hero">
        <img src={`${P}/silo-project.jpg`} alt="Industrial silo project by E.G. & I Construction" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${STEEL}22 0%, ${STEEL}CC 65%, ${STEEL}F5 100%)` }} />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-40 w-full">
          <motion.div {...fadeUp} className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: ORANGE }}>Cebu &middot; Since 1993</span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-tight text-white font-black">Three Decades of Industrial Construction.</h1>
            <p className="mt-5 text-lg text-white/85 leading-relaxed max-w-xl">
              Warehouses, storage silos, pipelines and foundation work — heavy industrial and infrastructure projects built and delivered across Cebu.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#contact" data-testid="egi-hero-cta" className="px-7 py-3.5 text-sm font-bold text-white" style={{ background: ORANGE }}>Request a Proposal</a>
              <a href="#projects" data-testid="egi-hero-projects" className="border border-white/40 px-7 py-3.5 text-sm font-bold text-white hover:bg-white/10 transition-colors">Our Projects</a>
            </div>
            <p className="mt-7 text-sm text-white/70" data-testid="egi-trust-signal">
              <Star size={14} className="inline -mt-0.5" style={{ color: ORANGE }} /> 4.8 Google Rating · 12 Reviews · Cebu
            </p>
          </motion.div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
        <motion.div {...fadeUp} className="max-w-xl mb-14">
          <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: ORANGE }}>What We Do</span>
          <h2 className="mt-3 text-4xl sm:text-5xl font-black leading-snug">Capabilities</h2>
        </motion.div>
        <div className="grid sm:grid-cols-3 gap-10">
          {[
            [Warehouse, "Warehousing & Storage", "Multi-unit dry storage warehouses and industrial storage facilities, built to spec."],
            [Boxes, "Silos & Bulk Storage", "Grain silo and bulk-storage structures for agricultural and industrial clients."],
            [HardHat, "Foundation & Civil Works", "Foundation, formwork and pipeline / utility excavation for larger builds."],
          ].map(([Icon, t, b]) => (
            <motion.div key={t} {...fadeUp}>
              <Icon size={26} style={{ color: ORANGE }} />
              <h3 className="mt-4 text-lg font-bold">{t}</h3>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: `${INK}90` }}>{b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="py-20 md:py-28" style={{ background: STEEL }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-xl mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: ORANGE }}>Our Work</span>
            <h2 className="mt-3 text-4xl sm:text-5xl text-white font-black leading-snug">Selected Projects</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              { img: `${P}/warehouse-project.jpg`, label: "8-Unit Dry Storage Warehouse Project" },
              { img: `${P}/silo-project.jpg`, label: "Grain silo / bulk storage structure" },
              { img: `${P}/pipeline-project.jpg`, label: "Pipeline and utility excavation" },
              { img: `${P}/foundation-project.jpg`, label: "Foundation and formwork stage" },
            ].map((it) => (
              <motion.div key={it.img} {...fadeUp} data-testid="egi-project-item" className="overflow-hidden">
                <img src={it.img} alt={it.label} className="w-full h-60 object-cover" loading="lazy" />
                <p className="mt-3 text-sm text-white/70">{it.label}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-10 border-t border-white/15 pt-8">
            <h3 className="text-xl font-bold text-white">Documented Road Performance</h3>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/70">Government CPES records show a 90.90% “Very Satisfactory” final rating for the Argao–Moalboal Road upgrading project and an 87.80 visit score for Bacalso Avenue national-road rehabilitation.</p>
          </motion.div>
        </div>
      </section>

      {/* PROJECT INQUIRY */}
      <section id="contact" className="py-20 md:py-28" style={{ background: CREAM }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-14">
          <motion.div {...fadeUp}>
            <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: ORANGE }}>Get In Touch</span>
            <h2 className="mt-3 text-4xl font-black leading-snug">Request a Proposal</h2>
            <ul className="mt-8 space-y-6">
              <li className="flex items-start gap-4">
                <MapPin size={20} style={{ color: ORANGE }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Address</div><div>Cebu City, Cebu</div></div>
              </li>
              <li className="flex items-start gap-4">
                <Facebook size={20} style={{ color: ORANGE }} className="mt-0.5 shrink-0" />
                <div><div className="text-xs uppercase tracking-wide" style={{ color: `${INK}60` }}>Facebook</div><a href={FB} target="_blank" rel="noopener noreferrer" data-testid="egi-facebook-link" className="hover:underline">facebook.com/EGIConstructionCorp</a></div>
              </li>
            </ul>
          </motion.div>
          <motion.div {...fadeUp}>
            <ConstructionInquiryForm
              testidPrefix="egi"
              accent={ORANGE}
              accentText="#FFFFFF"
              heading="Request a Proposal"
              subheading="Warehouse, silo, pipeline or foundation work — tell us about the project and our team will follow up."
              projectTypes={["Warehouse / Storage", "Silo / Bulk Storage", "Pipeline / Civil Works", "Other Industrial"]}
              messengerUrl={FB}
            />
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14" style={{ background: "#111820", color: "#FFFFFF99" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={`${P}/logo.jpg`} alt={NAME} className="h-10 w-10 rounded-full object-cover" />
              <span className="text-white text-lg font-black">E.G. &amp; I CONSTRUCTION</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed">Industrial and infrastructure construction based in Cebu, established 1993.</p>
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-3">Contact</div>
            <p className="text-sm">Cebu City, Cebu</p>
            <div className="flex gap-4 mt-4">
              <a href={FB} target="_blank" rel="noopener noreferrer" aria-label="Facebook"><Facebook size={18} className="hover:text-white transition-colors" /></a>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-10 pt-6 border-t border-white/10 text-xs">
          <p>This is an unsolicited concept website created by MySiteCare. It is not operated by or affiliated with {NAME}. Project details and availability should be confirmed directly with the company.</p>
        </div>
      </footer>

      <a href={FB} target="_blank" rel="noopener noreferrer" data-testid="construction-sticky-contact"
        className="lg:hidden fixed bottom-4 inset-x-4 z-50 px-6 py-3.5 text-center text-sm font-bold text-white shadow-xl" style={{ background: ORANGE }}>
        Message E.G. &amp; I
      </a>
      <ConceptCTAPill slug={SLUG} />
    </div>
  );
}
